import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import { PackageSelector } from "@/components/creditos/PackageSelector";
import { quantityAllowed, type PartnerPackageCatalog } from "@shared/credit-packages";
import { useMetodosPagamento } from "@/hooks/use-metodos-pagamento";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { apiErrorMessage } from "@/lib/api-error";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLocation } from "wouter";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { VEICULO_TIPO_LABELS } from "@/lib/constants";
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  ShoppingCart,
  Banknote,
  CreditCard,
  FileText,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { VALOR_MINIMO_CREDITO } from "@/lib/split-pricing";

const METODO_ICONES: Record<MetodoPagamentoAsaas, React.ReactNode> = {
  pix: <Banknote className="w-5 h-5" />,
  boleto: <FileText className="w-5 h-5" />,
  cartao_credito: <CreditCard className="w-5 h-5" />,
};
type MetodoPagamentoAsaas = "pix" | "boleto" | "cartao_credito";
const ASAAS_TAXAS = {
  pix: { label: "PIX", descricao: "Aprovação imediata" },
  boleto: { label: "Boleto", descricao: "Até 3 dias úteis" },
  cartao_credito: { label: "Cartão de Crédito", descricao: "Receba em até 32 dias" },
} satisfies Record<MetodoPagamentoAsaas, { label: string; descricao: string }>;
interface PartnerQuote { id: string; baseCents: number; processingFeeCents: number; totalCents: number; expiresAt: string; usingFallback: boolean }

export default function ComprarCreditos() {
  const { profile, user } = useAuth();
  const { modulos, isLoading: modulosLoading } = useModulos();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const empresaId = profile?.empresaId;
  const empresaTipo = profile?.empresa?.tipo ?? "";

  const catalogQuery = useQuery<PartnerPackageCatalog>({
    queryKey: ["/api/creditos/pacotes", empresaId],
    queryFn: async () => (await apiRequest("GET", "/api/creditos/pacotes")).json(),
    staleTime: 0,
    enabled: !!empresaId && empresaTipo === "PARCEIRO_DE_NEGOCIOS" && modulos.creditosPrePagos,
  });
  const catalog = catalogQuery.data;

  const [quantidades, setQuantidades] = useState<Record<string, number>>({
    auto_leve: 0,
    moto: 0,
    caminhao: 0,
    onibus: 0,
  });
  const [metodoPagamento, setMetodoPagamento] =
    useState<MetodoPagamentoAsaas>("pix");
  const metodosPermitidos = useMetodosPagamento();
  const [quote, setQuote] = useState<PartnerQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState<"normal" | "package">("normal");

  if (!modulosLoading && !modulos.creditosPrePagos) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <ShoppingCart className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            Módulo não disponível
          </h2>
          <p className="text-muted-foreground max-w-sm">
            O módulo de Créditos Pré-Pagos não está ativo para sua vistoriadora.
          </p>
        </div>
      </Shell>
    );
  }

  if (empresaTipo !== "PARCEIRO_DE_NEGOCIOS") {
    return (
      <Shell>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
          <p className="text-muted-foreground mb-6">
            Apenas parceiros de negócios podem comprar créditos.
          </p>
          <Button onClick={() => setLocation("/creditos")}>
            Voltar para Créditos
          </Button>
        </div>
      </Shell>
    );
  }

  const temPrecoParaTipo = (tipo: string) => catalog?.prices[tipo] != null;
  const precoConfigurado = Object.keys(VEICULO_TIPO_LABELS).some(
    temPrecoParaTipo,
  );

  if (modulosLoading || catalogQuery.isLoading) return <Shell><div className="flex items-center justify-center gap-2 py-20" role="status"><Loader2 className="h-5 w-5 animate-spin" />Carregando pacotes...</div></Shell>;
  if (catalogQuery.isError || !catalog) return <Shell><div className="mx-auto max-w-lg space-y-4 py-12" role="alert"><p>Não foi possível carregar os pacotes e preços. Tente novamente.</p><Button onClick={() => catalogQuery.refetch()}>Tentar novamente</Button></div></Shell>;

  if (!precoConfigurado) {
    return (
      <Shell>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            Preços não configurados
          </h2>
          <p className="text-muted-foreground mb-6">
            A vistoriadora ainda não configurou os preços para compra de créditos.
          </p>
          <Button onClick={() => setLocation("/creditos")}>
            Voltar para Créditos
          </Button>
        </div>
      </Shell>
    );
  }

  // Quantidades efetivas: zera qualquer tipo sem preço configurado antes de
  // calcular totais, mesmo que o input (desabilitado) tenha sido burlado.
  const quantidadesValidas = Object.fromEntries(
    Object.keys(VEICULO_TIPO_LABELS).map((tipo) => [
      tipo,
      temPrecoParaTipo(tipo) ? (quantidades[tipo] ?? 0) : 0,
    ]),
  );

  const totalLaudos = Object.values(quantidadesValidas).reduce(
    (a, b) => a + b,
    0,
  );
  const totalBaseValor = Object.entries(quantidadesValidas).reduce((sum, [tipo, q]) => sum + q * (catalog.prices[tipo] ?? 0), 0) / 100;
  const quantidadePermitida = quantityAllowed(totalLaudos, catalog.policy);
  const normalAllowed = catalog.policy.allowCustom || !catalog.policy.stepEnabled;
  const packageMode = catalog.policy.stepEnabled && (!normalAllowed || purchaseMode === "package");

  const valorBase = (quote?.baseCents ?? Math.round(totalBaseValor * 100)) / 100;
  const valorTaxa = (quote?.processingFeeCents ?? 0) / 100;
  const valorTotal = (quote?.totalCents ?? Math.round(totalBaseValor * 100)) / 100;

  const handleQuantidadeChange = (tipo: string, qtd: number) => {
    if (!temPrecoParaTipo(tipo)) return;
    const novaQtd = Number.isFinite(qtd) ? Math.max(0, Math.min(999, Math.floor(qtd))) : 0;
    setQuote(null);
    setQuantidades((prev) => ({ ...prev, [tipo]: novaQtd }));
  };

  const handlePagar = async () => {
    if (!user?.id || !empresaId) {
      toast({
        title: "Erro",
        description: "Usuário ou empresa não identificada",
        variant: "destructive",
      });
      return;
    }
    if (!quantidadePermitida) {
      toast({
        title: "Quantidade Inválida",
        description: normalAllowed ? "Selecione pelo menos 1 laudo" : `Selecione um total múltiplo de ${catalog.policy.step} créditos.`,
        variant: "destructive",
      });
      return;
    }
    if (valorBase < VALOR_MINIMO_CREDITO) {
      toast({
        title: "Valor Mínimo Não Atingido",
        description: `O total da cobrança deve ser de pelo menos R$ ${VALOR_MINIMO_CREDITO.toFixed(2)}.`,
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      if (!quote) {
        const response = await apiRequest("POST", "/api/asaas/cotacoes/creditos", {
          quantities: quantidadesValidas, method: metodoPagamento, installments: 1,
        });
        setQuote(await response.json());
        return;
      }
      const response = await apiRequest("POST", `/api/asaas/cotacoes/${quote.id}/confirmar`, {});
      const data = await response.json();
      sessionStorage.setItem("ultima_compra_id", data.sourceId);
      window.location.href = data.checkoutUrl;
    } catch (error) {
      toast({
        title: "Erro ao Processar",
        description: apiErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/creditos")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Comprar Créditos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {packageMode ? "Escolha um pacote e distribua os créditos na triagem." : "Informe a quantidade desejada para cada tipo de veículo."} O pagamento é processado com segurança pelo Asaas.
            </p>
            {normalAllowed && catalog.policy.stepEnabled && <div role="group" aria-label="Modo de compra" className="flex flex-wrap gap-2">
              <Button variant={packageMode ? "outline" : "default"} aria-pressed={!packageMode} disabled={loading} onClick={() => { setPurchaseMode("normal"); setQuote(null); }}>Por veículo</Button>
              <Button variant={packageMode ? "default" : "outline"} aria-pressed={packageMode} disabled={loading} onClick={() => { setPurchaseMode("package"); setQuote(null); }}>Por pacote</Button>
            </div>}
            {packageMode && <PackageSelector catalog={catalog} quantities={quantidadesValidas} disabled={loading} onChange={(value) => { setQuantidades(value); setQuote(null); }} />}

            {/* Seleção de quantidades */}
            <div className="space-y-4 border-t pt-5">
              <h2 className="font-semibold">{packageMode ? "Distribuição do pacote" : "Quantidade por tipo de veículo"}</h2>
              <p className="text-sm text-muted-foreground">{packageMode ? "Confira a distribuição escolhida. Use a triagem do pacote para ajustá-la." : "Escolha as quantidades de laudos para cada tipo de veículo."}</p>
              {Object.entries(VEICULO_TIPO_LABELS).map(([tipo, info]) => (
                <div
                  key={tipo}
                  className="grid gap-3 p-4 border rounded-lg sm:flex sm:items-end sm:gap-4"
                >
                  <div className="flex-1">
                    <Label className="text-base font-semibold">{info}</Label>
                    {temPrecoParaTipo(tipo) ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        R$ {((catalog.prices[tipo] ?? 0) / 100).toFixed(2)} por laudo
                      </p>
                    ) : (
                      <p className="text-sm text-amber-600 mt-1">
                        Preço não configurado
                      </p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label
                        htmlFor={`qtd-${tipo}`}
                        className="text-xs mb-1 block"
                      >
                        Quantidade
                      </Label>
                      <Input
                        id={`qtd-${tipo}`}
                        type="number"
                        min="0"
                        max="999"
                        step="1"
                        value={quantidadesValidas[tipo]}
                        disabled={loading || packageMode || !temPrecoParaTipo(tipo)}
                        onChange={(e) =>
                          handleQuantidadeChange(
                            tipo,
                            Number(e.target.value),
                          )
                        }
                        className="w-20 text-center"
                      />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">
                        Subtotal
                      </p>
                      <p className="font-semibold">
                        R${" "}
                        {(
                          ((catalog.prices[tipo] ?? 0) / 100) *
                          quantidadesValidas[tipo]
                        ).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Seleção de forma de pagamento */}
            {totalLaudos > 0 && !quantidadePermitida && <Alert variant="destructive"><AlertDescription>O total deve ser múltiplo de {catalog.policy.step} créditos. Ajuste sua divisão antes de calcular.</AlertDescription></Alert>}
            {totalLaudos > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">
                  Forma de Pagamento
                </Label>
                <div className={cn("grid gap-3", metodosPermitidos.length > 1 ? "grid-cols-3" : "grid-cols-1")}>
                  {(
                    Object.entries(ASAAS_TAXAS) as [
                      MetodoPagamentoAsaas,
                      (typeof ASAAS_TAXAS)[MetodoPagamentoAsaas],
                    ][]
                  ).filter(([metodo]) => metodosPermitidos.includes(metodo)).map(([metodo, config]) => {
                    const selecionado = metodoPagamento === metodo;
                    return (
                      <button
                        key={metodo}
                        type="button"
                        disabled={loading}
                        onClick={() => { setMetodoPagamento(metodo); setQuote(null); }}
                        className={cn(
                          "flex flex-col items-center gap-2 p-3 rounded-lg border-2 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          selecionado
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border hover:border-muted-foreground",
                        )}
                      >
                        {METODO_ICONES[metodo]}
                        <span className="text-xs font-semibold">
                          {config.label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {config.descricao}
                        </span>
                        <span className="text-xs font-medium">
                          {selecionado && quote ? `Taxa: R$ ${valorTaxa.toFixed(2)}` : "Taxa calculada no servidor"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resumo com taxa informativa */}
            {totalLaudos > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {totalLaudos} crédito{totalLaudos > 1 ? "s" : ""}:
                  </span>
                  <span>R$ {valorBase.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Taxa de processamento:
                  </span>
                  <span className="text-amber-700">
                    {quote ? `R$ ${valorTaxa.toFixed(2)}` : "A calcular"}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg text-primary">
                  <span>{quote ? "Total a pagar:" : "Total estimado:"}</span>
                  <span>R$ {valorTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs text-muted-foreground">
                O total definitivo é calculado no servidor com a taxa vigente
                do Asaas antes da confirmação.
              </AlertDescription>
            </Alert>

            {/* Botões */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => setLocation("/creditos")}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handlePagar}
                disabled={
                  loading ||
                  !quantidadePermitida ||
                  valorBase < VALOR_MINIMO_CREDITO
                }
                className="flex-1 gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Aguarde...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    {quote ? `Confirmar e pagar ${valorTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}` : "Calcular total"}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
